import { GameReplayProvider, useGame } from "~/app";
import { Game } from "~/business";
import { XNotifications, useBusEvent } from "~/utils";
import {
  XCenteredAbsolute,
  XGameEvent,
  XPhase,
  XTypography,
} from "./components/design";
import {
  XActionSlots,
  XBattleInfos,
  XChoiceModal,
  XCombatModal,
  XDiscardModal,
  XEndGameModal,
  XIntrigueModal,
  XMenuBar,
  XOpponents,
  XPlayerInfos,
  XShopModal,
  XTechModal,
} from "./components/game";
import { XConflict } from "./components/game/XConflict";
import { XHelpModal } from "./components/game/XHelpModal";
import { XLookDeckModal } from "./components/game/XLookDeckModal";
import { XPlayerLeaderModal as XPlayerLeaderModal } from "./components/game/XPlayerLeaderModal";
import { XHistoryModal } from "./components/history";
import { useYourTurn } from "./components/yourTurn/useYourTurn";
import { useGraphicScale, useViewingPlayerOpt } from "./hook";
import { Colors, Spacing } from "./theme";
import { XAuthorizeUndoModal } from "./components/game/XAuthorizeUndoModal";

export const XGame = () => {
  const viewingPlayer = useViewingPlayerOpt();
  if (viewingPlayer) {
    return <XGameContent />;
  } else {
    return (
      <GameReplayProvider>
        <XGameContent />
      </GameReplayProvider>
    );
  }
};

function XGameContent() {
  const { isUpToDate, commands, stateChangeBus, addNotification } = useGame();
  const viewingPlayer = useViewingPlayerOpt();
  const { scale } = useGraphicScale();
  useYourTurn();
  useBusEvent(stateChangeBus, (event) => {
    const { game, undoState } = event;
    const ignoredEvents: Game["infoEvents"][0]["methodName"][] = [
      "endBuyPhase",
    ];
    game.infoEvents.forEach((event) => {
      if (!ignoredEvents.includes(event.methodName)) {
        addNotification(
          XGameEvent({ event: { ...event, game, undoState, isInfo: true } })
        );
      }
    });
    if (event.event.playerId !== viewingPlayer?.id) {
      addNotification(
        XGameEvent({ event: { ...event.event, undoState, game } })
      );
    }
  });

  if (!isUpToDate) {
    return (
      <XCenteredAbsolute>
        <XTypography style={{ fontSize: 24 }}>
          loading...{commands.length}
        </XTypography>
      </XCenteredAbsolute>
    );
  }

  return (
    <div
      className="App"
      style={{
        position: "relative",
        background: "url('/image/game-background.png')",
        width: "100%",
        height: "100%",
      }}
    >
      <XCenteredAbsolute
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          pointerEvents: "none",
        }}
      ></XCenteredAbsolute>
      <XMenuBar />

      <XCombatModal />
      <XDiscardModal />
      <XIntrigueModal />
      <XTechModal />
      <XShopModal />
      <XEndGameModal />
      <XHelpModal />
      <XPlayerLeaderModal />
      <XLookDeckModal />
      <XHistoryModal />
      <XChoiceModal />
      <XAuthorizeUndoModal />

      <div>
        <div
          style={{
            paddingLeft: Spacing.px8,
            display: "flex",
            gap: Spacing.px8,
          }}
        >
          <XActionSlots />
        </div>
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top right",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <div style={{ display: "flex", gap: 40, top: 10 }}>
              <XBattleInfos />
              <XOpponents />
            </div>
            <div
              style={{
                padding: Spacing.px12,
                display: "flex",
                gap: Spacing.px32,
              }}
            >
              <XConflict />
            </div>
          </div>
        </div>
        <div style={{ position: "fixed", bottom: 0, left: 0, width: "100%" }}>
          <XPlayerInfos />
        </div>
      </div>
      <XPhase />
      <XNotifications
        style={{ gap: Spacing.px8, padding: Spacing.px12 }}
        notificationStyle={{
          boxShadow: "4px 4px 4px rgba(0, 0, 0, 0.25)",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          backgroundColor: Colors.common.white,
        }}
      />
    </div>
  );
}
